<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <!-- min-h rather than h: on a telephone the caption below the product name carries
         three things — version, house, who is signed in — and a fixed height forced them
         onto one line, where the house became "Hotel T…". Which house you are standing in
         is the one fact on this bar that changes what Start writes onto a room, so it
         gets a second line rather than an ellipsis. -->
    <header class="sticky top-0 z-10 flex min-h-16 shrink-0 items-center gap-1 border-b border-default bg-default px-2 py-2 sm:px-4">
        <UIcon name="i-lucide-hotel" class="size-6 shrink-0 text-primary" />

        <div class="flex min-w-0 flex-1 flex-col leading-tight">
            <span class="truncate text-sm font-semibold text-highlighted">
                Imprion PMS <span class="font-normal text-muted">Housekeeping</span>
            </span>

            <span class="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted">
                <AppVersion class="shrink-0" />

                <!-- One house per shift, so the house is a caption rather than a control.
                     It only becomes one when the account actually reaches more than one,
                     and then it is a menu, not a select: a native select sheet on a phone
                     covers the screen to change something that changes once a day. -->
                <UDropdownMenu
                    v-if="properties.length > 1"
                    :items="propertyItems"
                    :content="{ align: 'start' }"
                    :ui="{ content: 'w-56' }"
                >
                    <UButton
                        :label="propertyName"
                        :aria-label="$t('property.property')"
                        :ui="{ label: 'truncate' }"
                        trailing-icon="i-lucide-chevron-down"
                        color="neutral"
                        variant="link"
                        size="xs"
                        class="max-w-full shrink-0 gap-0.5 p-0 font-medium text-default"
                    />
                </UDropdownMenu>
                <span v-else-if="propertyName" class="max-w-full shrink-0 truncate font-medium text-default">{{ propertyName }}</span>

                <!-- Who is signed in, on the phone itself.
                     These devices are shared: one telephone lives on the trolley and
                     changes hands at the end of a shift. Pressing Start writes a name
                     onto a room, so the name it will write has to be readable before
                     the button is pressed — not found out afterwards from the sheet. -->
                <span v-if="userName" class="flex max-w-full shrink-0 items-center gap-1">
                    <span class="text-dimmed">·</span>
                    <UIcon name="i-lucide-user" class="size-3 shrink-0" />
                    <span class="truncate">{{ userName }}</span>
                </span>
            </span>
        </div>

        <!-- Milo, for the supervisor. It stands before the settings buttons because it
             is the only control up here that is ever about WORK — the rest are about
             the browser — and after the house and the name because it answers about
             them.

             Nothing is drawn at all where the account is not a supervisor with the
             assistant: no disabled orb, no greyed button. `useAssistantAvailable`
             is the same test the drawer and Alt+J make, and libs/assistant.ts says why
             it asks for two permissions and not one. -->
        <MiloHead
            v-if="assistantAvailable"
            :state="orbState"
            @click="toggleDrawer"
        />

        <AssistantDrawer />

        <!-- Icon-only and size xl: this is pressed with a thumb, often through a glove,
             and a label would cost the width the house name needs. Each one names
             itself for a screen reader instead. -->
        <UButton
            :label="languageLabel"
            :aria-label="`${$t('nav.language')}: ${languageName}`"
            :title="`${$t('nav.language')}: ${languageName}`"
            :ui="{ label: 'font-semibold' }"
            color="neutral"
            variant="ghost"
            size="xl"
            @click="cycleLanguage"
        />

        <UButton
            :aria-label="`${$t('nav.theme')}: ${themeLabel}`"
            :title="`${$t('nav.theme')}: ${themeLabel}`"
            :icon="themeIcon"
            color="neutral"
            variant="ghost"
            size="xl"
            @click="cycleTheme"
        />

        <UButton
            :aria-label="$t('nav.logout')"
            :title="$t('nav.logout')"
            icon="i-lucide-log-out"
            color="neutral"
            variant="ghost"
            size="xl"
            @click="logout"
        />

        <!-- Non-blocking progress hint: a corridor has bad signal often enough that a
             blocking spinner would be the screen most of the shift. -->
        <div
            v-if="appStore.isLoaderActive()"
            class="absolute inset-x-0 bottom-0 h-0.5 animate-pulse bg-primary"
        />
    </header>
</template>

<script lang="ts" setup>
    import type { DropdownMenuItem } from '@nuxt/ui'
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRouter } from 'vue-router'

    import AppVersion from '@/components/app/AppVersion.vue'
    import AssistantDrawer from '@/components/app/AssistantDrawer.vue'
    import MiloHead from '@/components/app/MiloHead.vue'
    import { useAssistant, useAssistantAvailable } from '@/libs/assistant'
    import { useAppStore } from '@/stores/app'

    const { t }    = useI18n()
    const router   = useRouter()
    const appStore = useAppStore()

    const { orbState, toggleDrawer } = useAssistant()

    const assistantAvailable = useAssistantAvailable()

    // The house list is resolved once per session in @/libs/session, before any screen
    // renders — every screen below works inside one house, and on a single-house
    // install nobody should have to choose it at all. On an account the API refuses
    // /properties to, the list is empty and this is simply a caption with nothing in
    // it; the screens themselves say what is missing.
    const properties = computed(() => appStore.properties)

    const userName = computed(() => appStore.userData?.name ?? '')

    const propertyName = computed(() =>
        properties.value.find(property => property.id === appStore.propertyId)?.name ?? '')

    const propertyItems = computed((): DropdownMenuItem[] => properties.value.map(property => ({
        label:    property.name,
        icon:     property.id === appStore.propertyId ? 'i-lucide-check' : undefined,
        onSelect: () => selectProperty(property.id),
    })))

    const selectProperty = (id: string) => {
        if (id === appStore.propertyId) return

        appStore.setPropertyId(id)
        // A full reload rather than a reactive cascade, exactly as the reception portal
        // does it: whatever is on the screen was loaded for the other house, and so was
        // the room list every task on it is named from.
        window.location.reload()
    }

    // Three states, one button: Light → Dark → System → Light. Icon-only here, so the
    // current mode is named on the tooltip and for a screen reader.
    const themeOrder = ['light', 'dark', 'auto'] as const

    const themeIcon = computed(() => {
        if (appStore.colorMode === 'light') return 'i-lucide-sun'
        if (appStore.colorMode === 'dark')  return 'i-lucide-moon'

        return 'i-lucide-monitor'
    })

    const themeLabel = computed(() => {
        if (appStore.colorMode === 'light') return t('nav.theme_light')
        if (appStore.colorMode === 'dark')  return t('nav.theme_dark')

        return t('nav.theme_system')
    })

    const cycleTheme = () => {
        const current = themeOrder.indexOf(appStore.colorMode as typeof themeOrder[number])

        appStore.colorMode = themeOrder[(current + 1) % themeOrder.length]
    }

    // Two languages, so one button that swaps them rather than a select to open. The
    // button shows the code it is on now; the full name is on the tooltip and for a
    // screen reader, because "SL" alone is not a word.
    const languages = ['sl', 'en'] as const

    const languageLabel = computed(() => appStore.locale.toUpperCase())

    const languageName = computed(() => t(`nav.language_${appStore.locale}`))

    const cycleLanguage = () => {
        const current = languages.indexOf(appStore.locale as typeof languages[number])

        appStore.changeLocale(languages[(current + 1) % languages.length])
    }

    const logout = () => {
        appStore.setLogout()
        router.push({ name: 'login' })
    }
</script>
